import { createApi } from './axios';
import type { TenantSkinId } from '../pages/tenant/miniapp-theme-form';
import { useTenantAuth, TenantInfo, TenantUser } from '../stores/auth.tenant';
import { requireCurrentTenantSession, TenantSessionChangedError } from '../utils/tenant-session';

const api = createApi(
  () => useTenantAuth.getState().jwt,
  (jwt) => useTenantAuth.getState().logoutIfCurrent(jwt),
);

/**
 * OSS 直传失败原因 → 人话。OSS 拒绝时返回的是 XML（`<Code>…</Code>`），
 * 直接把 `HTTP 403` 抛给用户等于没说，运营/客户根本无从下手。
 */
function explainOssError(status: number, xml: string): string {
  const code = /<Code>([^<]+)<\/Code>/.exec(xml)?.[1] ?? '';
  const map: Record<string, string> = {
    // 实测：OSS 是**整包收完**才判有效期，所以这条一定发生在漫长等待之后（见后端 policyTtlSeconds）
    AccessDenied: xml.includes('Policy expired')
      ? '上传超时：文件太大 / 上行网速偏慢，凭证已过期。建议压缩后重试，或换更快的网络'
      : '上传被拒绝（凭证校验失败），请刷新页面重试',
    EntityTooLarge: '文件超过大小上限',
    RequestTimeout: '网络中断，上传未完成，请重试',
    InvalidAccessKeyId: '存储服务配置异常，请联系技术支持',
  };
  return map[code] || `上传失败（HTTP ${status}${code ? ` ${code}` : ''}），请重试`;
}

/**
 * 浏览器直传 OSS：sts 拿签名 → 文件直接 POST 给 OSS → commit 写 media_asset。
 * 绕开服务端中转（不再把整个文件经 nginx→nest 缓冲再转 OSS），大文件快很多、不吃服务器内存。
 * 依赖 OSS bucket CORS 允许 POST（已配 `*`）。返回 { url, key } 与原服务端直传契约一致。
 *
 * 用 XHR 不用 fetch（换回，勿"现代化"成 fetch）：**fetch 拿不到上传进度**，
 * 而视频动辄几百 MB，没有进度条时界面完全静止，用户只能理解为「卡死了」——生产 E 端企业简介
 * 传视频就是这么被判定成故障的。`xhr.upload.onprogress` 是浏览器端唯一的上传进度通道。
 */
async function uploadToOss(
  file: File | Blob,
  purpose: string,
  onProgress?: (percent: number) => void,
): Promise<{ url: string; key: string }> {
  const expectedJwt = useTenantAuth.getState().jwt;
  requireCurrentTenantSession(expectedJwt, useTenantAuth.getState().jwt);
  const mime = (file as File).type || 'application/octet-stream';
  const size = file.size;
  // 1. 拿 POST Policy 签名 + 目标 key
  const sts = await api
    .post<{ form_fields: Record<string, string>; upload_url: string; object_key: string }>(
      '/oss/sts',
      { purpose, size, mime },
    )
    .then((r) => r.data);
  // 2. 直传 OSS：form_fields 全透传，file 必须最后 append（OSS POST Policy 要求）
  const fd = new FormData();
  Object.entries(sts.form_fields).forEach(([k, v]) => fd.append(k, v));
  fd.append('file', file);
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', sts.upload_url, true);
    // 不设 xhr.timeout：超时判定交给后端 policy 有效期（按体积给，最长 60min）+ 用户自己取消，
    // 前端设固定超时会把「正在正常推进的慢速大文件上传」误杀（M 端踩过同款坑）
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(explainOssError(xhr.status, xhr.responseText || '')));
      }
    };
    xhr.onerror = () => reject(new Error('网络中断，上传未完成，请重试'));
    xhr.onabort = () => reject(new Error('上传已取消'));
    xhr.send(fd);
  });
  requireCurrentTenantSession(expectedJwt, useTenantAuth.getState().jwt);
  // 3. commit 写 media_asset 留痕 + 拿公网 url；commit 失败不阻断（对象已在 OSS，url 可由 upload_url+key 推出）
  let url = `${sts.upload_url}/${sts.object_key}`;
  try {
    const committed = await api
      .post<{ url?: string }>('/oss/commit', { object_key: sts.object_key, purpose, mime, size })
      .then((r) => r.data);
    if (committed?.url) url = committed.url;
  } catch (error) {
    if (error instanceof TenantSessionChangedError) throw error;
    /* noop */
  }
  requireCurrentTenantSession(expectedJwt, useTenantAuth.getState().jwt);
  return { url, key: sts.object_key };
}

/** 工作台 Tab 可见成员摘要。 */
export interface WorkbenchTabViewer {
  membership_id: string;
  user_id: string;
  /** 展示名（名片真名优先，与用户列表「姓名」同口径）。 */
  nickname: string | null;
  /** 展示手机（名片手机优先）。 */
  phone: string | null;
}

/** 工作台 Tab（服务端返回的单条，带 id 与顺序）。 */
export interface WorkbenchTab {
  id: string;
  /** 槽位字母（A/B/C…）：行的稳定编号，跟随行、不随排序变化。 */
  slot: string;
  name: string;
  visible: boolean;
  /** 展示顺序，0 起（与数组下标一致）。 */
  sort_order: number;
  /** 勾选的图表应用模块编码。 */
  module_codes: string[];
  viewers: WorkbenchTabViewer[];
}

/** 提交给后端的工作台 Tab（无 id —— 整表覆盖，服务端按 slot upsert）。 */
export interface WorkbenchTabInput {
  /** 槽位字母；不传则由后端补最小空缺字母。 */
  slot?: string;
  name: string;
  visible: boolean;
  module_codes?: string[];
  viewer_membership_ids?: string[];
}

/** 工作台 Tab 可勾选的图表应用模块（与后端 workbench-chart-modules 对齐）。 */
export const WORKBENCH_CHART_MODULE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'business_school', label: '商学院' },
  { value: 'hr_admin', label: '人事行政' },
  { value: 'promo', label: '品牌宣传' },
  { value: 'market', label: '市场部' },
  { value: 'market_daily', label: '市场日报' },
  { value: 'expense', label: '费控报销' },
  { value: 'project_rd', label: '产品研发' },
  { value: 'investment', label: '招商' },
  { value: 'task', label: '任务督办' },
];

/** API 错误响应统一形状（NestJS ValidationPipe + class-validator 标准）。*/
/** ADR-34 §2.5 — 可管理租户（当前管理租户切换器用）。 */
export interface ManagedTenant {
  tenant_id: string;
  name: string;
  tenant_type: 'personal' | 'enterprise' | 'group' | 'government';
  role: 'tenant_owner' | 'tenant_admin';
  status: string;
}

export interface ApiErrorBody {
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

export interface Employee {
  id: string;
  tenant_id: string;
  username?: string;
  nickname?: string;
  role: string;
  // 列表展示字段 — 后端 listMembers 返回 membership + 花名册预设合成行
  phone?: string | null;
  email?: string | null;
  /** ADR-50：该员工**在本企业身份**的头像（后端已回退其本人默认头像） */
  avatar_url?: string | null;
  employee_title?: string | null;
  // ADR-46：部门名（membership.department_id → Department.name，唯一归属锚点）
  department?: string | null;
  department_id?: string | null;
  created_at: string;
  // ADR-46：已离职 tab（status=left）行携带
  left_at?: string | null;
}

export type CardProfileFields = {
  name_zh: string; phone: string; email: string; title: string;
  wechat_qr_url: string; douyin_id: string; xiaohongshu_id: string; shipinhao_id: string;
};
export type EmployeeProfilePatch = Partial<CardProfileFields> & { expected_revision: string };
export interface EmployeeProfileResponse {
  employee: { user_id: string; display_name: string };
  identity: { membership_id: string; tenant_id: string; tenant_name: string };
  card_id: string | null;
  audit_status: 'draft' | 'pending_review' | 'approved' | 'rejected' | null;
  pending_status: string | null;
  revision: string;
  profile: CardProfileFields;
}

/** ADR-49 企业元力池概览。池只是「充值中转 + 分配账本」，扣费仍扣员工个人账户 */
export interface CoinPool {
  tenant_id: string;
  balance_coins: number;
  total_in_coins: number;
  total_out_coins: number;
  rmb_per_coin: number;
  active_member_count: number;
  updated_at: string | null;
}

/** 充值档位：price_yuan 为档价，total_coins = 折算基数 + 该档赠送（赠送一并入池） */
export interface CoinPoolTier {
  promotion_id: string;
  name: string;
  price_yuan: number;
  base_coins: number;
  bonus_coins: number;
  total_coins: number;
}

/** 可分配成员（在职 membership；frozen 者列出但禁止勾选——分过去花不掉 = 资损） */
export interface AllocatableMember {
  user_id: string;
  nickname: string | null;
  phone: string | null;
  department: string | null;
  employee_title: string | null;
  balance_coins: number;
  frozen: boolean;
}

/** 一次分配动作（批次）。even 模式向下取整，余数留池 */
export interface CoinPoolAllocation {
  id: string;
  mode: 'even' | 'manual';
  total_coins: number;
  recipient_count: number;
  per_capita_coins: number | null;
  remainder_coins: number;
  balance_after: number;
  operator_user_id: string;
  operator_name?: string | null;
  note: string | null;
  created_at: string;
}

export interface CoinPoolAllocationItem {
  user_id: string;
  nickname: string | null;
  phone: string | null;
  coins: number;
}

/** 名片模版：M 端名片背景图；platform=系统推荐 / tenant=本企业自建 */
export interface CardTemplateItem {
  id: string;
  style: 'dark' | 'light';
  name: string | null;
  image_url: string;
  source: 'platform' | 'tenant';
  tenant_id?: string | null;
  display_order: number;
  active: boolean;
}

/** ADR-46：组织架构树节点（GET /departments/tree） */
export interface DepartmentNode {
  id: string;
  parent_id: string | null;
  name: string;
  display_order: number;
  description: string | null;
  member_count: number;
  children: DepartmentNode[];
}

export interface Card {
  id: string;
  user_id: string;
  tenant_id: string;
  is_primary: boolean;
  audit_status: 'draft' | 'pending_review' | 'approved' | 'rejected';
  rejected_reason: string | null;
  name_zh: string;
  name_en: string | null;
  title: string | null;
  company: string | null;
  phone: string | null;
  email: string | null;
  bio_richtext: string | null;
  hidden: boolean;
  created_at: string;
  updated_at: string;
  // 「已发布 + 待审草稿」分离：编辑已通过名片时新内容存这里，pending_status 标记待审态
  pending_status?: 'pending_review' | 'rejected' | null;
  // listPendingReview 附带：管理员实际要审的内容（场景 B 取待审草稿，场景 A 取 live 字段）
  review_content?: Partial<Card> | null;
}

export interface Highlight {
  id: string;
  tenant_id: string;
  /** 内容域（V5）：'highlight'=企业亮点(图/视频) | 'news'=企业资讯(图文 only)。建后不可改 */
  category?: 'highlight' | 'news';
  cover_url: string;
  title: string;
  content_type: 'video_text' | 'poster_text';
  video_url: string | null;
  body_richtext: string;
  /** 宣传图片（≤9 张，资讯详情页正文下方；同步 M 端） */
  image_urls?: string[] | null;
  /** 企业资讯是否关联公众号文章（仅 news 域） */
  link_official_account?: boolean;
  /** 公众号文章链接；未关联时为空 */
  official_account_article_url?: string | null;
  status: 'draft' | 'published' | 'hidden_by_user' | 'force_hidden_by_admin';
  forced_hidden_reason: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface OfficialVideo {
  id: string;
  tenant_id: string;
  title: string;
  video_url: string;
  cover_url?: string | null;
  status: 'published' | 'unpublished';
  created_at: string;
  updated_at: string;
}

export interface PlanSubscription {
  id: string;
  tenant_id: string;
  plan_id: string;
  total_seats: number;
  source: 'self_purchased' | 'group_granted';
  starts_at: string;
  expires_at: string;
  plan?: { id: string; code: string; name: string; price_yuan: number };
}

export interface PlanAiFeatures {
  daily_brief_per_day?: number;
  weekly_report_enabled?: boolean;
  execute_code_per_day?: number;
  image_gen_per_day?: number;
  proactive_reminders_enabled?: boolean;
  chat_high_enabled?: boolean;
}
export interface PlanLite {
  id: string;
  code: string;
  name: string;
  applicable_tenant_type: string;
  price_yuan: number;
  total_seats: number;
  grant_coins?: number; // ADR-38：开通赠送元力（套餐激活时一次性赠给创始人）
  duration_days?: number;
  max_subsidiaries?: number | null;
  ai_features?: PlanAiFeatures;
}

// ADR-41：套餐变更预览（续费叠加 / 升级折抵 / 降级）
export interface PlanChangePreview {
  action: 'new' | 'renew' | 'upgrade' | 'downgrade';
  amount_yuan: number;        // 本次应付
  list_price: number;         // 新套餐原价
  credit: number;             // 升级折抵
  remaining_days: number;     // 当前套餐剩余天数
  base_expires_at: string | null; // 续费叠加基准（旧到期日）
  new_expires_at: string;     // 激活后到期日
  next_renew_price: number;   // 下次续费价（= 原价）
  current_plan_name: string | null;
  new_plan_name: string;
}

export interface Order {
  id: string;
  order_no?: string;
  user_id: string;
  tenant_id: string | null;
  plan_id: string | null;
  product_type: string;
  amount_yuan: string | number;
  // ADR-41 升级折抵明细（套餐升级订单非空；amount_yuan 仍为实付）
  original_amount_yuan?: string | number | null;
  discount_yuan?: string | number | null;
  discount_kind?: string | null;
  payment_channel: string;
  status: 'pending' | 'paid' | 'refunded' | 'cancelled';
  paid_at: string | null;
  created_at: string;
  /** 开票状态 — 仅 paid 且 not_invoiced 的订单可申请开票。缺省视为 not_invoiced。 */
  invoice_status?: 'not_invoiced' | 'invoiced';
}

/** 统一支付入口返回（PaymentRouter）：mock 直付 / wxpay 返 JSAPI 参数。 */
export interface PayInitResult {
  order_id: string;
  mode: 'mock' | 'wxpay';
  prepay_id: string | null;
  jsapi_params: Record<string, string> | null;
  /** Native 扫码支付二维码内容（wxpay 模式返回；mock 为 null） */
  code_url?: string | null;
  paid_immediately: boolean;
}

/** 代开发票抬头类型 */
export type InvoiceTitleType = 'enterprise' | 'personal';

export interface InvoiceRequest {
  id: string;
  invoice_title: string;
  tax_id: string | null;
  amount_yuan: string | number;
  /** processing=处理中 | completed=已完成 | rejected=已驳回（关联订单已解锁可重新开票） */
  status: 'processing' | 'completed' | 'rejected' | string;
  /** 用户申请自填备注（旧 rejected 数据曾被驳回原因覆盖，前端兜底） */
  remark: string | null;
  /** 财务驳回原因（rejected 时必有，与用户备注分列） */
  rejection_reason?: string | null;
  created_at: string;
  resolved_at: string | null;
  /** 已完成的发票文件下载地址（PDF），processing 时为 null。 */
  invoice_file_url?: string | null;
}

export interface FormLead {
  id: string;
  visitor_name: string;
  visitor_phone: string;
  visitor_company: string | null;
  visitor_title: string | null;
  visitor_message: string | null;
  submitter_wx_nickname: string;
  submitter_wx_avatar: string;
  card_id: string;
  custom_data?: Record<string, any>;
  created_at: string;
  /** 跟进状态：followed=已跟进 | not_followed=未跟进。缺省视为 not_followed。 */
  follow_status?: 'followed' | 'not_followed';
  /** 老板跟进备注（单条覆盖，访客不可见）。null=从未写过，''=已清空。 */
  owner_note?: string | null;
  /** 备注最后更新时间（UTC instant，展示按京时格式化）。 */
  note_updated_at?: string | null;
}

export type CustomFieldType = 'text_short' | 'text_long' | 'select_single' | 'select_multi' | 'date';

export interface CustomField {
  /** 标识 key 由后端自生成（field_xxxx）；新建字段提交时无此值，保存后由响应回填 */
  key?: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  options?: string[];
}

export interface FormSchema {
  tenant_id: string;
  custom_fields: CustomField[];
  updated_at: string | null;
}

export interface TenantRelation {
  id: string;
  group_id: string;
  enterprise_id: string;
  status: 'pending' | 'active' | 'dissolving' | 'dissolved';
  initiated_by: string;
  bound_at: string | null;
  dissolve_at: string | null;
  hard_dissolve_at: string | null;
  rejected_reason: string | null;
  created_at?: string;
  group?: { id: string; name: string; status: string };
  enterprise?: { id: string; name: string; status: string };
}

export interface DisplayAdminItem {
  relation_id: string;
  enterprise_id: string;
  name: string;
  relation_status: 'active' | 'dissolving';
  enabled: boolean;
  display_image_key: string | null;
  display_image_url: string | null;
  display_order: number;
}

export interface DisplayPatch {
  enabled?: boolean;
  display_image_key?: string | null;
}

export interface SeatGrant {
  id: string;
  group_id: string;
  enterprise_id: string;
  seats: number;
  source_subscription_id: string;
  expires_at: string;
  created_at: string;
  revoked_at: string | null;
  enterprise?: { id: string; name: string };
}

export interface SeatAssignment {
  id: string;
  enterprise_id: string;
  user_id: string;
  source: 'self_purchased' | 'group_granted';
  source_grant_id: string | null;
  assigned_at: string;
  released_at: string | null;
  user?: { id: string; nickname: string | null; username: string | null; phone: string | null };
  /** ADR-37 显示名回退链解析结果（nickname → 本人名片 name_zh → 花名册 preset.name_zh）。
   *  服务端算好下发，**不要在前端重拼** —— 与员工列表页同源。三级都空时为 null。 */
  display_name?: string | null;
}

export interface SeatCapacity {
  self_seats: number;
  granted_out_seats: number; // 集团已下发给子公司（占自购池）
  self_usable_seats: number; // 本部可用自购 = self_seats - granted_out_seats
  granted_seats: number; // 子公司收到的集团下发
  total_seats: number; // 本租户实际可分配 = self_usable_seats + granted_seats
  assigned_seats: number;
  available_seats: number;
}

export interface GroupBrief { id: string; name: string; created_at: string; }

// Login Log (V1#67) — kept for LoginLogsPage
export type LoginResult = 'success' | 'failed_wrong_password' | 'failed_account_locked' | 'failed_unknown';
export interface LoginLog {
  id: string;
  user_id: string;
  user_phone: string;
  user_nickname?: string | null; // 后端 join users.nickname 展示用
  ip: string;
  user_agent: string;
  login_method?: 'password' | 'sms' | 'email' | 'wechat' | string | null;
  result: LoginResult;
  failure_reason?: string | null;
  created_at: string;
}

// === AI 用量（T+1 rollup；企业=个人级，集团=企业级+个人级）===

// 与 admin-platform/src/api/platform.ts 的 AiUsageRow 同构（后端 ai-usage rows 契约）——改字段两端同步（review C9）
export interface AiUsageRow {
  key: string;
  /** group_by=user → 成员姓名（ADR-37 回退链，可能 null）；group_by=tenant → 公司名 */
  label?: string | null;
  /** group_by=user 时返回，便于区分同名成员 */
  phone?: string | null;
  trial_coins: string;
  paid_coins: string;
  total_coins: string;
  call_count: number;
}

// === Phase 5c types ===

// ADR-26 修订：审计配置权收回 A 总后台，E 端只读（订阅页知情提示用）。
// keyword_alert 档位已废除（关键词告警改平台级无条件扫描）。
export interface TenantAuditConfig {
  tenant_id: string;
  level: 'off' | 'full_audit';
  updated_at: string | null;
  allowed_levels: Array<'off' | 'full_audit'>;
}

export interface PendingReviewItem {
  id: string;
  name: string;
  phone: string | null;
  company: string | null;
  title: string | null;
  confidence: number;
  created_at: string;
}

// === Phase 3c types ===
// Department 类型/接口已下线（部门口径方案 A：统一花名册「部门」文本）

export interface Announcement {
  id: string;
  tenant_id: string;
  title: string;
  body_md: string;
  pinned: boolean;
  published: boolean;
  published_at: string | null;
  created_at: string;
  /** 公告类型 */
  type?: 'notice' | 'policy' | 'activity' | 'award';
  /** 通知范围：all=全体员工 | custom=指定部门/人员 */
  notify_scope?: 'all' | 'custom';
  /** custom 时：完整勾选的部门 id（含未来新增成员，由后端按部门展开）*/
  notify_dept_ids?: string[];
  /** custom 时：单独勾选的员工 user_id（部门内部分人）*/
  notify_user_ids?: string[];
}

export interface TrendPoint {
  date: string; // 'MM-DD'
  count: number;
}

export interface EmployeeActiveRank {
  user_id: string;
  name: string;
  title: string | null;
  score: number;
}

export type DashboardRange = '7d' | '30d' | 'month';

export interface DashboardOverview {
  range: DashboardRange;
  // KPI
  visitors_today: number;
  exchanges_this_week: number;
  leads_this_month: number;
  active_employees_today: number;
  employees_total: number;
  // 图表
  visitor_trend: TrendPoint[];
  leads_trend: TrendPoint[];
  employee_active_top10: EmployeeActiveRank[];
  // 待办
  cards_pending_review: number;
  published_announcements: number; // 已发布在架公告总数（原 unread_announcements 命名失实）
  subscription: {
    plan_name: string | null;
    expires_at: string | null;
    days_left: number | null;
    renew_due: boolean;
  };
}

export type TrainingItemKey = 'smart_call' | 'super_employee' | 'written' | 'scenario' | 'presentation';

export interface TrainingItemMetrics {
  key: TrainingItemKey;
  label: string;
  avg_score: number | null;
  passed: number;
  pass_total: number;
  pass_rate: number | null;
}

export type TrainingItemMetricsMap = Record<TrainingItemKey, TrainingItemMetrics>;

export interface TrainingWeekSummary {
  week_no: number | null;
  label: string;
  assessments: number;
  details: number;
  employees: number;
  items: TrainingItemMetricsMap;
}

export interface TrainingEmployeeSummary {
  employee_id: string | null;
  employee_name: string;
  attempts: number;
  assessments: number;
  last_at: string | null;
  items: TrainingItemMetricsMap;
}

export interface TrainingAssessmentSummary {
  assessment_type: string;
  course_name: string;
  assessments: number;
  details: number;
  employees: number;
  items: TrainingItemMetricsMap;
}

export interface TrainingRecentDetail {
  h3_object_id: string;
  employee_name: string | null;
  smart_call_score: number | null;
  smart_call_passed: string | null;
  super_employee_score: number | null;
  super_employee_passed: string | null;
  written_score: number | null;
  written_passed: string | null;
  scenario_result: string | null;
  scenario_passed: string | null;
  presentation_result: string | null;
  presentation_passed: string | null;
}

export interface TrainingRecentAssessment {
  parent_object_id: string;
  assessment_type: string;
  course_name: string;
  week_no: number | null;
  created_by: string | null;
  h3_created_at: string | null;
  details: TrainingRecentDetail[];
}

export interface H3yunTrainingDashboard {
  credential_bound: boolean;
  latest_synced_at: string | null;
  totals: {
    assessments: number;
    details: number;
    employees: number;
    weeks: number;
  };
  items: TrainingItemMetricsMap;
  weekly: TrainingWeekSummary[];
  employees: TrainingEmployeeSummary[];
  assessments: TrainingAssessmentSummary[];
  recent: TrainingRecentAssessment[];
  filter_options: {
    weeks: number[];
    assessment_types: string[];
    courses: string[];
  };
}

export interface H3yunTrainingSyncResult {
  tenant_id: string;
  main_count: number;
  detail_count: number;
  skipped_count: number;
  pages: number;
  deleted_main_count: number;
  deleted_detail_count: number;
  synced_at: string;
}

// Gov Messages (V1#32-33)
export interface GovMessage {
  id: string;
  tenant_id: string;
  card_id: string;
  sender_name: string;
  sender_phone: string | null;
  body: string;
  audit_status: 'pending' | 'approved' | 'rejected';
  reply: string | null;
  reviewer_id: string | null;
  reviewed_at: string | null;
  created_at: string;
}

// ─── Roles / Permissions（参照 admin-platform）───
// 预定义角色 企业管理员 / 普通员工；is_builtin=true 不允许改权限矩阵，可改 description。
export interface TenantRole {
  id: string;
  code: string;
  name: string;
  description: string;
  permissions: string[]; // ['*'] = 全部；否则页面级 key 列表
  is_builtin: boolean;
  staff_count: number;
  created_at: string;
  updated_at: string;
}

export interface TenantPermissionGroup {
  key: string;
  label: string;
  // privileged: 仅预定义角色可拥有的特权权限（镜像 A 端 PermissionDef，编辑器可据此禁勾）
  perms: { key: string; label: string; privileged?: boolean }[];
}

export const tenantApi = {
  login: (username: string, password: string) =>
    api.post<{ jwt: string; user: TenantUser; tenant: TenantInfo; has_seat: boolean; must_change_password: boolean }>('/tenant/admin/auth/login', { username, password }).then(r => r.data),
  // SMS 验证码登录契约（待后端实现）
  // GET  /tenant/admin/auth/captcha  → { captcha_id, image_base64, ttl_seconds }
  // POST /tenant/admin/auth/sms/send  body: { phone, captcha_id, captcha_text } → { ok: true, ttl_seconds: 300 }
  //      错误码：invalid_captcha / captcha_expired / sms_rate_limited / sms_send_failed
  // POST /tenant/admin/auth/login-by-phone  body: { phone, code } → 同 login 返回结构
  getCaptcha: () =>
    api.get<{ captcha_id: string; image_base64: string; ttl_seconds: number }>('/tenant/admin/auth/captcha').then(r => r.data),
  sendSmsCode: (phone: string, captcha_id: string, captcha_text: string) =>
    api.post<{ ok: true; ttl_seconds: number }>('/tenant/admin/auth/sms/send', { phone, captcha_id, captcha_text }).then(r => r.data),
  loginByPhone: (phone: string, code: string) =>
    api.post<{ jwt: string; user: TenantUser; tenant: TenantInfo; has_seat: boolean; must_change_password: boolean }>('/tenant/admin/auth/login-by-phone', { phone, code }).then(r => r.data),
  changePassword: (old_password: string, new_password: string) =>
    api.post<{ ok: true }>('/tenant/admin/auth/change-password', { old_password, new_password }).then(r => r.data),
  // ADR-34 §2.5 — 当前管理租户切换器
  listManagedTenants: () =>
    api.get<ManagedTenant[]>('/tenant/admin/auth/managed-tenants').then(r => r.data),
  switchTenant: (tenant_id: string) =>
    api.post<{ jwt: string; tenant: TenantInfo; managed_tenants: ManagedTenant[] }>(
      '/tenant/admin/auth/switch-tenant', { tenant_id },
    ).then(r => r.data),
  // 刷新后水合当前登录用户（store 仅持久化 jwt，user 丢失会让 useIsOwner 误判非创始人）
  getMe: () => api.get<{ id: string; username?: string; nickname?: string; role: string }>('/tenant/admin/auth/me').then(r => r.data),
  getProfile: () => api.get<TenantInfo>('/tenant/admin/profile').then(r => r.data),
  // Phase 7-G 扩展字段：credit_code / industry / scale / contact_name / contact_phone / logo_url / business_scope / address / website
  updateProfile: (data: Partial<TenantInfo>) =>
    api.patch<TenantInfo>('/tenant/admin/profile', data).then(r => r.data),
  getEnterpriseShop: () => api.get<{ shop_id: string; shop_name: string; status: string; visible: boolean; inherited_from_tenant_id?: string; inherited_from_tenant_name?: string; can_manage_visibility?: boolean } | null>('/tenant/admin/enterprise-shop').then(r => r.data),
  setEnterpriseShopVisibility: (visible: boolean) => api.patch('/tenant/admin/enterprise-shop/visibility', { visible }).then(r => r.data),
  getKingkongFeatures: () => api.get<{
    eligible: boolean;
    tenant_id: string;
    tenant_type: string;
    features: Record<'smart_assistant' | 'smart_customer_service' | 'geo_matrix' | 'enterprise_shop', {
      enabled: boolean;
      local_enabled: boolean;
      platform_granted: boolean;
      inherited: boolean;
      inherited_from_tenant_id: string | null;
      inherited_from_tenant_name: string | null;
      can_manage: boolean;
    }>;
  }>('/tenant/admin/enterprise-shop/kingkong-features').then(r => r.data),
  setKingkongFeature: (featureKey: 'smart_assistant' | 'smart_customer_service' | 'geo_matrix' | 'enterprise_shop', enabled: boolean) =>
    api.patch(`/tenant/admin/enterprise-shop/kingkong-features/${featureKey}`, { enabled }).then(r => r.data),
  // Logo 上传 → 浏览器直传 OSS（见 uploadToOss）
  uploadLogo: (file: File) => uploadToOss(file, 'tenant_logo'),

  // 在线客服：头像 / 二维码同走浏览器直传，URL 经 updateProfile 落库
  uploadCustomerServiceAvatar: (file: File) => uploadToOss(file, 'customer_service_avatar'),
  uploadCustomerServiceQr: (file: File) => uploadToOss(file, 'customer_service_qr'),

  // ── 名片模版 / 小程序风格──
  /** 可选模版 = 系统推荐(active) + 本企业自建 */
  listCardTemplates: (style?: 'dark' | 'light') =>
    api.get<CardTemplateItem[]>('/tenant/admin/card-templates', { params: style ? { style } : {} }).then(r => r.data),
  createCardTemplate: (data: { style: 'dark' | 'light'; image_url: string; name?: string }) =>
    api.post<CardTemplateItem>('/tenant/admin/card-templates', data).then(r => r.data),
  deleteCardTemplate: (id: string) =>
    api.delete(`/tenant/admin/card-templates/${id}`).then(r => r.data),
  uploadCardTemplateImage: (file: File) => uploadToOss(file, 'card_template'),
  /** 企业统一小程序风格：{ style, template_dark_id, template_light_id }，保存后全员工生效 */
  getMiniappTheme: () =>
    api.get<{ style: 'dark' | 'light'; template_dark_id: string | null; template_light_id: string | null; theme_id?: TenantSkinId | null }>(
      '/tenant/admin/miniapp-theme',
    ).then(r => r.data),
  putMiniappTheme: (data: { style: 'dark' | 'light'; template_dark_id?: string; template_light_id?: string; theme_id?: TenantSkinId | null }) =>
    api.put('/tenant/admin/miniapp-theme', data).then(r => r.data),

  // ===== 工作台 Tab 配置（本企业小程序数据看板二级 Tab）=====
  listWorkbenchTabs: () =>
    api.get<{ items: WorkbenchTab[] }>('/tenant/admin/workbench-tabs').then(r => r.data),
  listWorkbenchTabMembers: (search?: string) =>
    api
      .get<{ items: WorkbenchTabViewer[] }>('/tenant/admin/workbench-tabs/members', {
        params: search?.trim() ? { search: search.trim() } : undefined,
      })
      .then((r) => r.data),
  /** 整表覆盖保存；传 `[]` 表示清空。 */
  saveWorkbenchTabs: (tabs: WorkbenchTabInput[]) =>
    api.put<{ items: WorkbenchTab[] }>('/tenant/admin/workbench-tabs', { tabs }).then(r => r.data),

  /**
   * 文件上传 → 浏览器直传 OSS（绕开服务端中转，大文件快很多）。
   * purpose: highlight_cover(图≤2MB) | highlight_video(≤100MB) | official_video(视频 ≤500MB)
   * → { url, key }（url 为 OSS 公共读直链，直接入表单）
   * onProgress: 0-100，视频类**务必传**——没有它界面全程静止，用户会当成卡死。
   */
  uploadFile: (
    file: File | Blob,
    purpose: 'highlight_cover' | 'highlight_poster' | 'highlight_video' | 'highlight_image' | 'official_video' | 'official_video_cover' | 'company_brief_image' | 'subsidiary_display_image',
    onProgress?: (percent: number) => void,
  ) => uploadToOss(file, purpose, onProgress),
  // ADR-34：listEmployees 带 search（按姓名/手机号），数据源已改 membership 驱动
  // ADR-46：status='left' → 已离职历史
  listEmployees: (params: { page?: number; page_size?: number; search?: string; status?: 'active' | 'left' }) =>
    api.get<{ items: Employee[]; total: number; page: number; page_size: number }>('/tenant/admin/employees', { params }).then(r => r.data),
  getEmployee: (id: string) =>
    api.get<Employee>(`/tenant/admin/employees/${id}`).then(r => r.data),
  getEmployeeProfile: (id: string) =>
    api.get<EmployeeProfileResponse>(`/tenant/admin/employees/${id}/profile`).then(r => r.data),
  updateEmployeeProfile: (id: string, input: EmployeeProfilePatch) =>
    api.patch<EmployeeProfileResponse>(`/tenant/admin/employees/${id}/profile`, input).then(r => r.data),
  uploadEmployeeWechatQr: (file: File) => uploadToOss(file, 'wechat_qr'),
  // createEmployee 已删（ADR-34 §2.6：员工只能经花名册邀请产生，E 端无新建入口）
  /** 重置员工密码 — 返回明文一次性展示。*/
  resetEmployeePassword: (id: string) =>
    api.post<{
      user_id: string;
      phone: string | null;
      nickname: string | null;
      initial_password_plaintext: string;
    }>(`/tenant/admin/employees/${id}/reset-password`).then(r => r.data),
  /** ADR-46：员工页编辑部门/职位（写 membership 唯一归属锚点；department_id 空串=未分配）。*/
  updateEmployeeRoster: (id: string, data: { department_id?: string; employee_title?: string }) =>
    api.patch<{ user_id: string; department_id: string | null; department: string | null; employee_title: string | null }>(
      `/tenant/admin/employees/${id}/roster`, data,
    ).then(r => r.data),
  /** ADR-46：移除成员（离职）— left + 释放席位 + 撤销预设（花名册可再邀请复职）。*/
  removeEmployee: (id: string) =>
    api.post<{ ok: boolean }>(`/tenant/admin/employees/${id}/remove`).then(r => r.data),

  // ── ADR-46 组织架构（部门树） ──
  getDepartmentTree: () =>
    api.get<DepartmentNode[]>('/tenant/admin/departments/tree').then(r => r.data),
  createDepartment: (data: { name: string; parent_id?: string; description?: string }) =>
    api.post<{ id: string }>('/tenant/admin/departments', data).then(r => r.data),
  updateDepartment: (id: string, data: { name?: string; parent_id?: string; description?: string }) =>
    api.patch(`/tenant/admin/departments/${id}`, data).then(r => r.data),
  reorderDepartments: (ordered_ids: string[]) =>
    api.patch('/tenant/admin/departments/reorder', { ordered_ids }).then(r => r.data),
  deleteDepartment: (id: string) =>
    api.delete(`/tenant/admin/departments/${id}`).then(r => r.data),

  // status：pending(默认,待审队列) | approved | rejected | all（已审核记录保留可查）
  listPendingCards: (params: { page?: number; page_size?: number; status?: string }) =>
    api.get<{ items: Card[]; total: number; page: number; page_size: number }>('/tenant/admin/cards/pending-review', { params }).then(r => r.data),
  approveCard: (id: string) => api.post<Card>(`/tenant/admin/cards/${id}/approve`, {}).then(r => r.data),
  rejectCard: (id: string, reason: string) => api.post<Card>(`/tenant/admin/cards/${id}/reject`, { reason }).then(r => r.data),

  /** ?category（V5 亮点/资讯双页各自传域）：'highlight'=企业亮点 | 'news'=企业资讯；不传=全量（兼容） */
  listHighlights: (category?: 'highlight' | 'news') =>
    api.get<Highlight[]>('/tenant/admin/highlights', { params: category ? { category } : undefined }).then(r => r.data),
  /** 数量配额（平台全局限制；V5 起按域分开计数、共享同一上限）：enabled=false 时不限。*/
  getHighlightQuota: (category?: 'highlight' | 'news') =>
    api.get<{ enabled: boolean; max_count: number; used: number; can_create: boolean }>(
      '/tenant/admin/highlights/quota',
      { params: category ? { category } : undefined },
    ).then(r => r.data),
  createHighlight: (data: Partial<Highlight>) => api.post<Highlight>('/tenant/admin/highlights', data).then(r => r.data),
  getHighlight: (id: string) => api.get<Highlight>(`/tenant/admin/highlights/${id}`).then(r => r.data),
  updateHighlight: (id: string, data: Partial<Highlight>) => api.patch<Highlight>(`/tenant/admin/highlights/${id}`, data).then(r => r.data),
  toggleHighlightStatus: (id: string) => api.post<Highlight>(`/tenant/admin/highlights/${id}/toggle-status`, {}).then(r => r.data),
  deleteHighlight: (id: string) => api.delete<Highlight>(`/tenant/admin/highlights/${id}`).then(r => r.data),
  /** 排序（V5 起 body 带 category 分域，防混排串写另一域顺序） */
  reorderHighlights: (ordered_ids: string[], category: 'highlight' | 'news' = 'highlight') =>
    api.post<Highlight[]>('/tenant/admin/highlights/reorder', { ordered_ids, category }).then(r => r.data),
  /** 批量上下架：action=publish 上架 / unpublish 下架（强制下架的会被后端跳过）。 */
  batchToggleHighlights: (ids: string[], action: 'publish' | 'unpublish') =>
    api.post<{ updated: number }>('/tenant/admin/highlights/batch-status', { ids, action }).then(r => r.data),
  /** 批量删除（强制下架的会被后端跳过）。 */
  batchDeleteHighlights: (ids: string[]) =>
    api.post<{ deleted: number }>('/tenant/admin/highlights/batch-delete', { ids }).then(r => r.data),

  // ─── 官方视频 ───
  listOfficialVideos: (params: { page?: number; page_size?: number; status?: 'published' | 'unpublished' }) =>
    api.get<{ items: OfficialVideo[]; total: number; page: number; page_size: number }>(
      '/tenant/admin/official-videos',
      { params },
    ).then(r => r.data),
  /** 上传官方视频 — title 为列表展示用的标题备注，video_url 为已上传地址，cover_url 为可选封面。 */
  uploadOfficialVideo: (data: { title: string; video_url: string; cover_url?: string }) =>
    api.post<OfficialVideo>('/tenant/admin/official-videos', data).then(r => r.data),
  /** 编辑官方视频 — 可改标题备注 / 替换视频 / 替换封面。 */
  updateOfficialVideo: (id: string, data: { title?: string; video_url?: string; cover_url?: string }) =>
    api.patch<OfficialVideo>(`/tenant/admin/official-videos/${id}`, data).then(r => r.data),
  /** 上下架切换：published ↔ unpublished。 */
  toggleOfficialVideoStatus: (id: string) =>
    api.post<OfficialVideo>(`/tenant/admin/official-videos/${id}/toggle-status`, {}).then(r => r.data),
  deleteOfficialVideo: (id: string) =>
    api.delete<{ ok: true }>(`/tenant/admin/official-videos/${id}`).then(r => r.data),

  getCurrentSubscription: () => api.get<PlanSubscription | null>('/tenant/admin/subscription/current').then(r => r.data),
  listSubscriptionHistory: () => api.get<PlanSubscription[]>('/tenant/admin/subscription/history').then(r => r.data),
  listApplicablePlans: () => api.get<PlanLite[]>('/tenant/admin/plans/applicable').then(r => r.data),
  /** ADR-41：套餐变更预览（续费叠加 / 升级折抵 / 降级），用于确认弹窗展示应付与到期。 */
  previewPlanOrder: (plan_id: string) =>
    api.get<PlanChangePreview>('/tenant/admin/orders/plan/preview', { params: { plan_id } }).then(r => r.data),

  createPlanOrder: (plan_id: string, payment_channel?: string) =>
    api.post<Order>('/tenant/admin/orders/plan', { plan_id, payment_channel }).then(r => r.data),
  /** 统一支付入口（与 miniapp 共用 PaymentRouter，mock/真实由服务端 PAYMENT_MODE 决定）。 */
  payOrder: (id: string) =>
    api.post<PayInitResult>(`/tenant/payment/orders/${id}/pay`).then(r => r.data),
  /** Native 扫码支付：返回 code_url 供网页渲染二维码（Web 后台无法调起微信 JSAPI）。 */
  payOrderNative: (id: string) =>
    api.post<PayInitResult>(`/tenant/payment/orders/${id}/pay-native`).then(r => r.data),
  /** 轮询单个订单（扫码支付后查 status 是否转 paid）。 */
  getOrder: (id: string) =>
    api.get<Order>(`/tenant/admin/orders/${id}`).then(r => r.data),
  listMyOrders: (params: { page?: number; page_size?: number; status?: string; product_type?: string; start_at?: string; end_at?: string }) =>
    api.get<{ items: Order[]; total: number; page: number; page_size: number }>('/tenant/admin/orders', { params }).then(r => r.data),

  listMyInvoices: (params: { page?: number; page_size?: number; status?: 'processing' | 'completed' | 'rejected' }) =>
    api.get<{ items: InvoiceRequest[]; total: number; page: number; page_size: number }>(
      '/tenant/admin/invoices',
      { params },
    ).then(r => r.data),
  /**
   * 按订单批量开具电子发票。
   * - order_ids：勾选的可开票订单（后端需校验均为本租户、status=paid、invoice_status=not_invoiced）
   * - amount_yuan：order_ids 金额合计（后端应以订单实际金额重算校验，不信任前端）
   * - title_type：enterprise=企业单位 / personal=个人非企业单位
   * - tax_id / invoice_title / email 必填，remark 选填
   * 成功后这些订单 invoice_status 应置 invoiced。
   */
  issueInvoiceForOrders: (data: {
    order_ids: string[];
    amount_yuan: number;
    title_type: InvoiceTitleType;
    invoice_title: string;
    tax_id: string;
    email: string;
    remark?: string;
  }) => api.post<InvoiceRequest>('/tenant/admin/invoices/issue', data).then(r => r.data),

  listLeads: (params: { page?: number; page_size?: number; status?: 'followed' | 'not_followed' }) =>
    api.get<{ items: FormLead[]; total: number; page: number; page_size: number }>('/tenant/admin/leads', { params }).then(r => r.data),
  /** 标记线索跟进状态。 */
  updateLeadFollowStatus: (id: string, follow_status: 'followed' | 'not_followed') =>
    api.patch<FormLead>(`/tenant/admin/leads/${id}`, { follow_status }).then(r => r.data),
  /** 保存/清空老板跟进备注（单条覆盖，''=清空）。 */
  updateLeadNote: (id: string, owner_note: string) =>
    api.patch<FormLead>(`/tenant/admin/leads/${id}`, { owner_note }).then(r => r.data),

  // ADR-21 联系表单 schema
  getFormSchema: () => api.get<FormSchema>('/tenant/admin/form-schema').then(r => r.data),
  putFormSchema: (custom_fields: CustomField[]) =>
    api.put<FormSchema>('/tenant/admin/form-schema', { custom_fields }).then(r => r.data),

  // 集团 / 绑定关系
  searchGroups: (q: string) =>
    api.get<GroupBrief[]>('/tenant/admin/group/groups/search', { params: { q } }).then(r => r.data),
  applyToGroup: (group_id: string, message?: string) =>
    api.post<TenantRelation>('/tenant/admin/group/apply', { group_id, message }).then(r => r.data),
  listRelations: (status?: string) =>
    api.get<{ perspective: 'group' | 'enterprise'; items: TenantRelation[] }>(
      '/tenant/admin/group/relations', { params: status ? { status } : {} }
    ).then(r => r.data),
  decideRelation: (id: string, approve: boolean, reason?: string) =>
    api.post<TenantRelation>(`/tenant/admin/group/relations/${id}/decision`, { approve, reason }).then(r => r.data),
  dissolveRelation: (id: string, reason?: string) =>
    api.post<TenantRelation>(`/tenant/admin/group/relations/${id}/dissolve`, { reason }).then(r => r.data),
  listSubsidiaryDisplay: () =>
    api.get<{ items: DisplayAdminItem[] }>('/tenant/admin/group/subsidiary-display').then(r => r.data),
  updateSubsidiaryDisplay: (relationId: string, patch: DisplayPatch) =>
    api.patch<DisplayAdminItem>(
      `/tenant/admin/group/subsidiary-display/${relationId}`,
      patch,
    ).then(r => r.data),
  reorderSubsidiaryDisplay: (ordered_relation_ids: string[]) =>
    api.post<{ ordered_relation_ids: string[] }>(
      '/tenant/admin/group/subsidiary-display/reorder',
      { ordered_relation_ids },
    ).then(r => r.data),
  cancelApplication: (id: string) =>
    api.post<TenantRelation>(`/tenant/admin/group/relations/${id}/cancel`).then(r => r.data),
  // createSubsidiary 已删：租户一律只在 A 端创建，
  //    E 端集团只管理与解绑已绑定的公司；后端端点同步下线。

  // 席位
  listGrants: () => api.get<SeatGrant[]>('/tenant/admin/seats/grants').then(r => r.data),
  grantSeats: (enterprise_id: string, seats: number) =>
    api.post<SeatGrant>('/tenant/admin/seats/grants', { enterprise_id, seats }).then(r => r.data),
  revokeGrant: (id: string) => api.delete<SeatGrant>(`/tenant/admin/seats/grants/${id}`).then(r => r.data),
  /** 集团关系页使用：TenantSeatGrant 层面的席位容量（已授权/已分配/可用数量）。 */
  getSeatCapacity: () => api.get<SeatCapacity>('/tenant/admin/seats/capacity').then(r => r.data),
  listAssignments: (released?: boolean) =>
    api.get<SeatAssignment[]>('/tenant/admin/seats/assignments', { params: released ? { released: true } : {} }).then(r => r.data),
  releaseSeat: (user_id: string) => api.post<SeatAssignment>('/tenant/admin/seats/release', { user_id }).then(r => r.data),

  // Login Logs (V1#67)
  listLoginLogs: (params: {
    page?: number;
    page_size?: number;
    user_id?: string;
    phone?: string;
    ip?: string;
    result?: LoginResult;
    start_at?: string;
    end_at?: string;
  }) =>
    api.get<{ items: LoginLog[]; total: number; page: number; page_size: number }>('/tenant/admin/login-logs', { params }).then(r => r.data),

  // AI 用量（企业=本租户个人级；集团=本部+active 子公司，可按 subsidiary_id 下钻）
  getAiUsage: (params: {
    start_date?: string;
    end_date?: string;
    user_id?: string;
    skill?: string;
    subsidiary_id?: string;
    group_by?: 'day' | 'skill' | 'user' | 'tenant';
  }) =>
    api.get<{ group_by: string; rows: AiUsageRow[] }>('/tenant/admin/ai-usage', { params }).then(r => r.data),

  // Phase 5c — audit config（ADR-26 修订后只读，配置权在 A 总后台）+ pending review
  getAuditConfig: () =>
    api.get<TenantAuditConfig>('/tenant/admin/audit-config').then(r => r.data),

  listPendingReview: () =>
    api.get<PendingReviewItem[]>('/tenant/admin/profiles/pending-review').then(r => r.data),
  reviewDecision: (id: string, decision: 'accept' | 'reject') =>
    api.post(`/tenant/admin/profiles/${id}/review-decision`, { decision }).then(r => r.data),

  // Phase 3c — CompanyBrief
  // 同步 M 端：brief_image_urls 宣传展示图片（≤6 张，名片主页简介下轮播）随简介一起保存
  updateCompanyBrief: (company_brief: string, brief_image_urls?: string[]) =>
    api.patch<TenantInfo>('/tenant/admin/profile', { company_brief, ...(brief_image_urls !== undefined ? { brief_image_urls } : {}) } as any).then(r => r.data),

  // 批二 — 栏目自定义标题（简介/视频/亮点）。空串=清空回退默认名。
  // V5 加 visible：栏目「是否在小程序首页显示」开关（简介/亮点/资讯，缺省 true）。
  getModuleConfigs: () =>
    api.get<Array<{ module_key: string; custom_display_name: string | null; visible?: boolean; home_display_count: number | null }>>('/tenant/admin/module-config').then(r => r.data),
  upsertModuleName: (module_key: 'company_brief' | 'official_video' | 'highlights' | 'news' | 'subsidiaries', custom_display_name: string) =>
    api.patch('/tenant/admin/module-config', { module_key, custom_display_name }).then(r => r.data),
  /** 只拨可见性开关（不带 custom_display_name，后端 PATCH 语义不动已设置的自定义名） */
  setModuleVisible: (module_key: 'company_brief' | 'highlights' | 'news', visible: boolean) =>
    api.patch('/tenant/admin/module-config', { module_key, visible }).then(r => r.data),
  setHomeDisplayCount: (module_key: 'highlights' | 'news' | 'subsidiaries', home_display_count: number) =>
    api.patch('/tenant/admin/module-config', { module_key, home_display_count }).then(r => r.data),

  // Phase 3c — Announcements
  listAnnouncements: (params?: { status?: 'published' | 'draft' }) =>
    api.get<Announcement[]>('/tenant/admin/announcements', { params }).then(r => r.data),
  getAnnouncement: (id: string) =>
    api.get<Announcement>(`/tenant/admin/announcements/${id}`).then(r => r.data),
  createAnnouncement: (data: Partial<Announcement>) =>
    api.post<Announcement>('/tenant/admin/announcements', data).then(r => r.data),
  updateAnnouncement: (id: string, data: Partial<Announcement>) =>
    api.patch<Announcement>(`/tenant/admin/announcements/${id}`, data).then(r => r.data),
  deleteAnnouncement: (id: string) =>
    api.delete<{ ok: boolean }>(`/tenant/admin/announcements/${id}`).then(r => r.data),
  /** 撤回已发布公告 → 回到草稿（published=false）。 */
  withdrawAnnouncement: (id: string) =>
    api.post<Announcement>(`/tenant/admin/announcements/${id}/withdraw`, {}).then(r => r.data),

  // Phase 3c — Dashboard
  getDashboardOverview: (range: DashboardRange = '30d') =>
    api.get<DashboardOverview>('/tenant/admin/dashboard/overview', { params: { range } }).then(r => r.data),

  // 氚云培训考核：每分钟服务端自动同步；手动同步按钮走同一个租户级同步服务。
  getH3yunTrainingDashboard: (params?: { week_no?: number; assessment_type?: string }) =>
    api.get<H3yunTrainingDashboard>('/tenant/admin/h3yun-training/dashboard', { params }).then(r => r.data),
  syncH3yunTraining: () =>
    api.post<H3yunTrainingSyncResult>('/tenant/admin/h3yun-training/sync').then(r => r.data),

  // Phase 3c — Recharge order；target_user_id = 代充值目标员工（缺省充给当前登录管理员自己）
  // credit_coins（ADR-40）= 代充值元力精确入元力数（仅代充值生效）；amount_yuan = 应付 RMB。
  createRechargeOrder: (amount_yuan: number, payment_channel?: string, target_user_id?: string, credit_coins?: number) =>
    api.post<Order>('/tenant/admin/orders/recharge', { amount_yuan, payment_channel, target_user_id, credit_coins }).then(r => r.data),

  // 当前元力比例（rmb_per_coin），代充值元力 Modal 展示 + 算应付 RMB 用。
  getCurrencyRate: () =>
    api.get<{ rmb_per_coin: number }>('/tenant/admin/orders/recharge/currency-rate').then(r => r.data),

  // ── ADR-49 企业元力池 ────────────────────────────────────────────────────
  // 池充值 → 一键平均/按人分配 → 落员工个人账户（不可回收）。分配 owner-only。
  getCoinPool: () =>
    api.get<CoinPool>('/tenant/admin/coin-pool').then(r => r.data),

  /** 充值档位（A 端「充值赠送」维护，与 M 端个人充值同一套） */
  listCoinPoolTiers: () =>
    api.get<{ rmb_per_coin: number; items: CoinPoolTier[] }>(
      '/tenant/admin/coin-pool/recharge-tiers',
    ).then(r => r.data),

  /**
   * 池充值建单。二选一：promotion_id（档位，含赠送）或 coins（自定义，精确入池不计促销）。
   * 不传金额 —— 应付金额一律服务端算（档位取档价、自定义按比例），多传字段会 400。
   * 对公转账必带 voucher_url + remark（A 端凭它审核「确认到账」）。
   */
  createCoinPoolRechargeOrder: (body: {
    coins?: number;
    promotion_id?: string;
    payment_channel?: 'wxpay' | 'bank_transfer';
    voucher_url?: string;
    remark?: string;
  }) => api.post<Order>('/tenant/admin/coin-pool/recharge-orders', body).then(r => r.data),

  /** 对公转账凭证上传（浏览器直传 OSS，与 A 端建租户凭证同 purpose） */
  uploadBankVoucher: (file: File) => uploadToOss(file, 'bank_voucher'),

  /** 可分配成员（不分页：企业规模 ≤ 千人，前端本地过滤，且要支持「默认全选」） */
  listAllocatableMembers: (search?: string) =>
    api.get<{ items: AllocatableMember[]; total: number }>(
      '/tenant/admin/coin-pool/allocatable-members', { params: { search } },
    ).then(r => r.data),

  allocateCoinPool: (body: {
    mode: 'even' | 'manual';
    client_request_id: string;
    user_ids?: string[];
    items?: { user_id: string; coins: number }[];
    note?: string;
  }) =>
    api.post<CoinPoolAllocation & { allocation_id: string; idempotent: boolean }>(
      '/tenant/admin/coin-pool/allocations', body,
    ).then(r => r.data),

  listCoinPoolAllocations: (params: { page?: number; page_size?: number; mode?: string }) =>
    api.get<{ items: CoinPoolAllocation[]; total: number; page: number; page_size: number }>(
      '/tenant/admin/coin-pool/allocations', { params },
    ).then(r => r.data),

  getCoinPoolAllocation: (id: string) =>
    api.get<CoinPoolAllocation & { items: CoinPoolAllocationItem[] }>(
      `/tenant/admin/coin-pool/allocations/${id}`,
    ).then(r => r.data),

  // Phase 3c — Exports（owner-only；走 axios 实例：401 统一登出 / 报错中文化）
  downloadExport: (kind: 'employees' | 'leads' | 'orders') =>
    api.get<Blob>(`/tenant/admin/exports/${kind}.xlsx`, { responseType: 'blob' }).then(r => r.data),

  // Gov Messages (V1#32-33)
  listGovMessages: (params: { status?: string; page?: number; page_size?: number }) =>
    api.get<{ items: GovMessage[]; total: number; page: number; page_size: number }>(
      '/tenant/admin/government-messages', { params },
    ).then(r => r.data),
  approveGovMessage: (id: string, reply?: string) =>
    api.post<GovMessage>(`/tenant/admin/government-messages/${id}/approve`, { reply }).then(r => r.data),
  rejectGovMessage: (id: string) =>
    api.post<GovMessage>(`/tenant/admin/government-messages/${id}/reject`, {}).then(r => r.data),

  // ─── Roles / Permissions ───
  listTenantRoles: () =>
    api.get<TenantRole[]>('/tenant/admin/roles').then(r => r.data),
  getTenantRole: (id: string) =>
    api.get<TenantRole>(`/tenant/admin/roles/${id}`).then(r => r.data),
  updateTenantRole: (id: string, data: Partial<{ name: string; description: string; permissions: string[] }>) =>
    api.patch<TenantRole>(`/tenant/admin/roles/${id}`, data).then(r => r.data),

  // 后端契约：GET /tenant/admin/permissions 返回 { groups: TenantPermissionGroup[] }
  // （与 A 端 /admin/permissions 统一外层包裹，见 contracts 文档 §十）；解包 .groups 返回数组。
  listTenantPermissions: () =>
    api.get<{ groups: TenantPermissionGroup[] }>('/tenant/admin/permissions').then(r => r.data.groups),
  /**
   * 把角色批量授权给一组员工 — RoleAssignModal 用。
   * 后端预期：把这些 user_ids 的 users.role 更新为该 role 对应的 code（tenant_admin / tenant_member）。
   */
  assignTenantRole: (role_id: string, user_ids: string[]) =>
    api.post<{ ok: true; assigned_count: number }>(`/tenant/admin/roles/${role_id}/assign`, { user_ids }).then(r => r.data),

  // ─── ADR-28 P3：员工花名册（SeatPreset）───
  reinviteSeatPreset: (phone: string) =>
    api.post<SeatPreset>('/tenant/admin/seat-presets/reinvite', { phone }).then(r => r.data),
  listSeatPresets: (status?: 'reserved' | 'claimed' | 'revoked' | 'declined') =>
    api.get<SeatPreset[]>('/tenant/admin/seat-presets', { params: status ? { status } : {} }).then(r => r.data),
  /** 花名册页使用：含 reserved_presets 字段，精确到预设层面（区别于 getSeatCapacity 的授予层面）。 */
  getSeatPresetCapacity: () =>
    api.get<SeatPresetCapacity>('/tenant/admin/seat-presets/capacity').then(r => r.data),
  importSeatPresets: (items: Array<{ phone: string; name_zh: string; title?: string; department?: string }>) =>
    api.post<{ created: number; revived: number; skipped: number; blocked: string[] }>('/tenant/admin/seat-presets', { items }).then(r => r.data),
  revokeSeatPreset: (id: string) =>
    api.delete<SeatPreset>(`/tenant/admin/seat-presets/${id}`).then(r => r.data),
};

export interface SeatPreset {
  id: string;
  tenant_id: string;
  phone: string;
  name_zh: string;
  title: string | null;
  department: string | null;
  status: 'reserved' | 'claimed' | 'revoked' | 'declined';
  claimed_user_id: string | null;
  claimed_at: string | null;
  created_at: string;
  revoked_at?: string | null;
}

export interface SeatPresetCapacity extends SeatCapacity {
  reserved_presets: number;
}
